import React, { useMemo } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import {
  StaffOptionFragment,
  useGetAppointmentTypeStaffQuery,
} from "./AppointmentTypeStaffSelect-generated.hooks";

interface Value {
  userUuid: string;
  firstName: string;
  lastName: string;
}

interface AppointmentTypeStaffSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends Value,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends ReactSelectControllerProps<
    TFieldValues,
    TName,
    Option,
    IsMulti,
    Group
  > {
  appointmentTypeUuid?: string;
}

function AppointmentTypeStaffSelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends Value,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: AppointmentTypeStaffSelectProps<
    TFieldValues,
    TName,
    Option,
    IsMulti,
    Group
  > &
    (ReactSelectSyncProps | ReactSelectAsyncProps),
) {
  const { appointmentTypeUuid, ...other } = props;

  const staffQuery = useGetAppointmentTypeStaffQuery({
    variables: { uuid: appointmentTypeUuid },
    skip: !appointmentTypeUuid,
  });

  const getOptionLabel = (option: StaffOptionFragment) => {
    return `${option.firstName} ${option.lastName}`;
  };

  const getOptionValue = (option: StaffOptionFragment) => {
    return option.userUuid as string;
  };

  const options = useMemo(() => {
    return staffQuery?.data?.getAppointmentTypeItem?.staff || [];
  }, [staffQuery.data]);

  return (
    <ReactSelectController
      menuPortalTarget={document.body}
      {...other}
      options={options as any}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
    />
  );
}

export default AppointmentTypeStaffSelect;
